import { Fragment, type ReactNode } from 'react';

function renderInline(text: string): ReactNode[] {
  const parts: React.ReactNode[] = [];
  const re = /(\*\*(.+?)\*\*|\*(.+?)\*|\[([^\]]+)\]\((https?:\/\/[^)]+)\))/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (match[2]) parts.push(<strong key={key}>{match[2]}</strong>);
    else if (match[3]) parts.push(<em key={key}>{match[3]}</em>);
    else if (match[4] && match[5]) {
      parts.push(
        <a key={key} href={match[5]} target="_blank" rel="noreferrer" className="text-primary underline">
          {match[4]}
        </a>
      );
    }
    key += 1;
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function parseRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());
}

function isSepRow(line: string): boolean {
  return /^\s*\|?\s*:?-{3,}/.test(line);
}

function isTableLine(line: string): boolean {
  return /^\s*\|.+\|\s*$/.test(line);
}

export function ObdKbMarkdown({ markdown }: { markdown: string }) {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const blocks: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i += 1;
      continue;
    }

    if (isTableLine(line)) {
      const tableLines: string[] = [];
      while (i < lines.length && isTableLine(lines[i])) {
        tableLines.push(lines[i]);
        i += 1;
      }
      const rows = tableLines.filter((l) => !isSepRow(l)).map(parseRow);
      if (rows.length > 0) {
        const header = rows[0];
        const body = rows.slice(1);
        blocks.push(
          <div key={key} className="overflow-x-auto max-w-full overscroll-x-contain my-3">
            <table className="w-max min-w-full text-sm border-collapse">
              <thead className="bg-bgSecondary text-textLight">
                <tr>
                  {header.map((cell, idx) => (
                    <th key={idx} className="px-3 py-2 text-left whitespace-nowrap border border-border">
                      {renderInline(cell)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {body.map((row, rIdx) => (
                  <tr key={rIdx} className="border-t border-border">
                    {row.map((cell, cIdx) => (
                      <td key={cIdx} className="px-3 py-2 whitespace-nowrap border border-border">
                        {renderInline(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        key += 1;
      }
      continue;
    }

    const heading = line.match(/^(#{1,4})\s+(.+)$/);
    if (heading) {
      const level = heading[1].length;
      const cls =
        level === 1 ? 'text-xl font-semibold mt-4 mb-2' :
        level === 2 ? 'text-lg font-semibold mt-4 mb-2' :
        'text-base font-medium mt-3 mb-1';
      blocks.push(
        <div key={key} className={cls}>
          {renderInline(heading[2])}
        </div>
      );
      key += 1;
      i += 1;
      continue;
    }

    if (/^---+$/.test(line.trim())) {
      blocks.push(<hr key={key} className="my-4 border-border" />);
      key += 1;
      i += 1;
      continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i += 1;
      }
      blocks.push(
        <ul key={key} className="list-disc pl-5 space-y-1 my-2">
          {items.map((item, idx) => (
            <li key={idx}>{renderInline(item)}</li>
          ))}
        </ul>
      );
      key += 1;
      continue;
    }

    const para: string[] = [];
    while (i < lines.length && lines[i].trim() && !lines[i].startsWith('#') && !isTableLine(lines[i]) && !/^\s*[-*]\s+/.test(lines[i])) {
      para.push(lines[i]);
      i += 1;
    }
    blocks.push(
      <p key={key} className="my-2 leading-relaxed">
        {para.map((p, idx) => (
          <Fragment key={idx}>
            {idx > 0 ? ' ' : null}
            {renderInline(p)}
          </Fragment>
        ))}
      </p>
    );
    key += 1;
  }

  return <div className="text-sm text-textDefault">{blocks}</div>;
}
